import { BrandLogo } from './BrandLogo';
import { Link, useLocation } from 'react-router-dom';
import { brandCopy } from '@skytech/shared';
import { analyticsConfigured, track } from '../../features/analytics/analytics';
import { InstallApp } from '../../features/pwa/InstallApp';
import { useCategories } from '../../features/products/api';
import { usePickupLocations } from '../../features/pickup/api';
import { groupHours } from '../../features/pickup/hours';
import { whatsappLink } from '../../lib/whatsapp';

const footerLink =
  'inline-flex min-h-11 items-center rounded-sm text-sm text-white/80 transition-colors hover:text-white hover:underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white';
const utilityLink =
  'inline-flex min-h-11 items-center rounded-sm text-xs text-white/75 hover:text-white hover:underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white';
const careLinks = [
  ['/delivery', 'Delivery & pickup'],
  ['/returns', 'Returns & refunds'],
  ['/terms', 'Ordering & payment'],
  ['/orders', 'Your orders'],
] as const;

/** Company, navigation and contact information; mobile-first targets per SRS §4.9. */
export function Footer(): JSX.Element {
  const { pathname } = useLocation();
  const { data: categories = [] } = useCategories();
  const { data: locations = [] } = usePickupLocations();
  const location = locations[0];
  const phone = location?.phone || brandCopy.phoneE164;
  const dial = phone.startsWith('254') ? `+${phone}` : phone;
  const displayPhone = phone.replace(/^\+?254(\d{3})(\d{6})$/, '+254 $1 $2');
  const wa = whatsappLink('Hello SkyTech Solutions, I need help with a product or order.');
  const address = location?.addressLine.startsWith(location.name)
    ? location.addressLine.slice(location.name.length).replace(/^[\s–—-]+/, '')
    : location?.addressLine;
  const landmark =
    location?.landmark &&
    !location.addressLine.toLowerCase().includes(location.landmark.toLowerCase())
      ? location.landmark
      : null;

  return (
    <footer
      className={`${pathname === '/' ? 'mt-0' : 'mt-16'} border-t border-frost bg-navy-900 text-white`}
      aria-label="SkyTech store information"
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-6">
        <div className="flex flex-col gap-5 border-b border-white/15 py-6 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
          <div>
            <h2 className="text-xl font-light tracking-wide sm:text-2xl">
              A little help choosing?
            </h2>
            <p className="mt-1.5 text-sm leading-relaxed text-white/75">
              Talk to us about products, delivery or pickup.
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            {wa && (
              <a
                href={wa}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => track('contact_click', { contact_method: 'whatsapp' })}
                className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-green-700 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-green-ink focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white sm:flex-none"
              >
                <FooterIcon kind="chat" />
                Chat on WhatsApp
              </a>
            )}
            <a
              href={`tel:${dial}`}
              onClick={() => track('contact_click', { contact_method: 'phone' })}
              className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-lg border border-white/30 px-4 py-2.5 text-sm text-white transition-colors hover:bg-white/10 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white sm:flex-none"
            >
              <FooterIcon kind="phone" />
              {displayPhone}
            </a>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-x-6 gap-y-8 py-8 md:grid-cols-4 lg:grid-cols-[1.15fr_.9fr_1fr_1.75fr] lg:gap-x-8">
          <div className="col-span-2 min-w-0 md:col-span-4 lg:col-span-1">
            <Link
              to="/"
              aria-label="SkyTech Solutions home"
              className="inline-flex min-h-11 items-center gap-3 rounded-sm focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white"
            >
              <BrandLogo inverse />
            </Link>
            <p className="mt-4 max-w-sm text-sm leading-relaxed text-white/80">
              {brandCopy.tagline}.
            </p>
            <Link to="/delivery" className={`${footerLink} mt-2 gap-2 text-xs`}>
              <FooterIcon kind="pin" />
              Nairobi store. Kenya-wide collection.
            </Link>
          </div>

          <nav aria-label="Footer shopping" className="min-w-0">
            <h2 className="text-xs font-medium tracking-[0.12em] text-white/70">SHOP</h2>
            <ul className="mt-3">
              <li>
                <Link to="/products" className={footerLink}>
                  All products
                </Link>
              </li>
              {categories
                .filter((c) => (c.totalProductCount ?? c.productCount ?? 0) > 0)
                .slice(0, 5)
                .map((c) => (
                  <li key={c.id}>
                    <Link to={`/category/${c.slug}`} className={footerLink}>
                      {c.name}
                    </Link>
                  </li>
                ))}
            </ul>
          </nav>

          <nav aria-label="Footer customer care" className="min-w-0">
            <h2 className="text-xs font-medium tracking-[0.12em] text-white/70">CUSTOMER CARE</h2>
            <ul className="mt-3">
              {careLinks.map(([to, text]) => (
                <li key={to}>
                  <Link to={to} className={footerLink}>
                    {text}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <section aria-labelledby="footer-visit" className="col-span-2 min-w-0 lg:col-span-1">
            <h2 id="footer-visit" className="text-xs font-medium tracking-[0.12em] text-white/70">
              VISIT & COLLECT
            </h2>
            <div className="mt-4 grid grid-cols-2 gap-5">
              <div className="min-w-0">
                <p className="text-sm font-medium">{location?.name ?? 'Our Nairobi store'}</p>
                <address className="mt-2 text-xs not-italic leading-relaxed text-white/80">
                  {address ?? brandCopy.locality}
                  {landmark && <span className="mt-1 block">{landmark}</span>}
                </address>
                {location?.mapUrl && (
                  <a
                    href={location.mapUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${footerLink} mt-2 gap-1 text-xs`}
                  >
                    Get directions
                    <FooterIcon kind="arrow" />
                  </a>
                )}
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-medium">Store hours</h3>
                {location?.hours ? (
                  <dl className="mt-3 space-y-2 text-xs text-white/80">
                    {groupHours(location.hours).map((row) => (
                      <div
                        key={row.label}
                        className="flex flex-wrap items-baseline justify-between gap-x-2 gap-y-0.5"
                      >
                        <dt>{row.label}</dt>
                        <dd className="whitespace-nowrap tabular-nums">{row.value}</dd>
                      </div>
                    ))}
                  </dl>
                ) : (
                  <p className="mt-2 text-xs leading-relaxed text-white/75">
                    Call to confirm opening hours.
                  </p>
                )}
                {location?.holidaysNote && (
                  <p className="mt-3 text-xs leading-relaxed text-white/70">
                    {location.holidaysNote}
                  </p>
                )}
                <p className="mt-2 text-xs text-white/70">Nairobi time (EAT)</p>
              </div>
            </div>
          </section>
        </div>
      </div>

      <div className="border-t border-white/15">
        <div className="mx-auto flex max-w-7xl flex-col gap-1 px-5 py-3 sm:px-6 lg:flex-row lg:items-center lg:justify-between lg:gap-5">
          <p className="py-2 text-xs text-white/70">
            © {new Date().getFullYear()} {brandCopy.name}. All rights reserved.
          </p>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-0 text-xs lg:pr-14">
            <Link to="/privacy" className={utilityLink}>
              Privacy policy
            </Link>
            <Link to="/terms" className={utilityLink}>
              Terms & conditions
            </Link>
            {analyticsConfigured() && (
              <button
                type="button"
                className={utilityLink}
                onClick={() => window.dispatchEvent(new Event('skytech-privacy-settings'))}
              >
                Privacy settings
              </button>
            )}
            <InstallApp />
            <a href="#main" className={`${utilityLink} gap-1`}>
              Back to top
              <FooterIcon kind="up" />
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterIcon({ kind }: { kind: 'chat' | 'phone' | 'pin' | 'arrow' | 'up' }) {
  const paths = {
    chat: <path d="M20 11.5a8 8 0 0 1-8 8 9 9 0 0 1-3.5-.7L4 20l1.2-4.5a8 8 0 1 1 14.8-4Z" />,
    phone: <path d="m8 3 2 5-2.5 2a14 14 0 0 0 6.5 6.5l2-2.5 5 2-1 4c-9 1-17-7-16-16Z" />,
    pin: (
      <>
        <path d="M19 10c0 5-7 11-7 11S5 15 5 10a7 7 0 1 1 14 0Z" />
        <circle cx="12" cy="10" r="2" />
      </>
    ),
    arrow: <path d="M7 17 17 7M7 7h10v10" />,
    up: <path d="m6 14 6-6 6 6" />,
  };
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-4 w-4 shrink-0"
    >
      {paths[kind]}
    </svg>
  );
}
